import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_ID = 'help-contact-form';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db4s1uhrqll58fj4oang';

const FAQS = [
  {
    q: '本站的影片都可以在线观看吗？',
    a: '本站为演示站点，播放区域仅用于展示界面与交互效果，不接入真实视频流。页面上的“模拟播放”按钮会弹出说明提示，完整影片请前往正规授权平台观看。',
  },
  {
    q: '为什么有些影片显示“功能演示”？',
    a: '因为本站所有影视作品名称与资料均为虚构演示内容，为避免误导用户，我们不提供任何实际播放、下载或资源分发功能。',
  },
  {
    q: '收藏的影片会消失吗？',
    a: '不会。收藏记录保存在你的本地浏览器缓存中，关闭浏览器后再次访问依然保留，但清除浏览器数据后收藏也会一并清除。',
  },
  {
    q: '如何快速找到想看的影片？',
    a: '你可以使用顶部的搜索框按影片名称、演员或关键词搜索；也可以在分类页使用地区、年份、类型进行多条件筛选，并按最新、最热、评分排序。',
  },
  {
    q: '内容分类会不会不准确？',
    a: '我们为每部演示影片都配置了地区、年份、类型等标签，分类页支持多维度组合筛选，确保结果与条件一一对应。',
  },
  {
    q: '手机端可以使用吗？',
    a: '可以。本站适配手机端浏览，顶部提供汉堡菜单，底部有快捷导航栏，分类页筛选会自动切换为弹层形式，方便触控操作。',
  },
];

interface FormStatus {
  state: 'idle' | 'submitting' | 'success' | 'error';
  message: string;
}

export default function HelpPage() {
  usePageMeta(
    '常见问题与帮助 - 亚洲中文字幕在线观看',
    '解答关于亚洲中文字幕在线观看的常见问题，如播放演示说明、收藏保存、搜索与筛选方法等，并提供反馈联系表单。',
  );

  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [formStatus, setFormStatus] = useState<FormStatus>({ state: 'idle', message: '' });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string | null)?.trim() ?? '';
    if (honeypot) {
      setFormStatus({ state: 'success', message: '感谢你的反馈，我们已经收到你的留言。' });
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setFormStatus({ state: 'submitting', message: '' });

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, typeof value === 'string' ? value : '');
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus({ state: 'success', message: '提交成功！感谢你的反馈，我们会尽快查看。' });
        form.reset();
      } else {
        setFormStatus({
          state: 'error',
          message:
            serverMsg && typeof serverMsg === 'string'
              ? `提交失败：${serverMsg}`
              : '提交失败，请稍后重试，或检查填写内容是否完整。',
        });
      }
    } catch {
      setFormStatus({ state: 'error', message: '网络异常，提交失败，请稍后重试。' });
    }
  };

  return (
    <Layout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '常见问题' }]} />

        <div className="mt-4 max-w-3xl">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
            常见问题与帮助
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            关于本站功能、演示范围与使用方法的常见疑问，都在这里。
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-question-line text-primary-500"></i>
              常见问题解答
            </h2>
            <div className="space-y-3">
              {FAQS.map((faq, index) => {
                const open = openIndex === index;
                return (
                  <div
                    key={faq.q}
                    className="rounded-lg bg-background-100 border border-background-200 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenIndex(open ? null : index)}
                      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                    >
                      <h3 className="text-sm md:text-base font-medium text-foreground-900">
                        {faq.q}
                      </h3>
                      <i
                        className={`shrink-0 text-foreground-500 transition-transform ${
                          open ? 'ri-subtract-line text-primary-500' : 'ri-add-line'
                        }`}
                      ></i>
                    </button>
                    {open && (
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-7">{faq.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-20 rounded-lg bg-background-100 border border-background-200 p-6">
              <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-mail-send-line text-primary-500"></i>
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500 leading-6">
                有建议、合作意向或内容问题，欢迎通过下方表单留言，我们会认真阅读每一条反馈。
              </p>

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    您的称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    邮箱地址
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    问题类型
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer transition-colors"
                  >
                    <option value="" disabled>
                      请选择类型
                    </option>
                    <option value="功能反馈">功能反馈</option>
                    <option value="内容问题">内容问题</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他建议">其他建议</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述您的想法或遇到的问题（最多 500 字）"
                    className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none transition-colors"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus.state === 'submitting'}
                  className="w-full h-11 flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus.state === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交反馈
                    </>
                  )}
                </button>

                {formStatus.state === 'success' && (
                  <p className="flex items-start gap-2 text-sm text-primary-600 bg-primary-100/60 border border-primary-200 rounded-md px-3 py-2.5">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    {formStatus.message}
                  </p>
                )}
                {formStatus.state === 'error' && (
                  <p className="flex items-start gap-2 text-sm text-accent-600 bg-accent-100/60 border border-accent-200 rounded-md px-3 py-2.5">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {formStatus.message}
                  </p>
                )}

                <p className="text-xs text-foreground-400 leading-5">
                  提交即表示你同意我们就该反馈与你联系。本站为演示站点，反馈信息仅用于演示说明。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}